import React, { useReducer, useContext, useEffect } from 'react';
import api from '../../utils/api';
import UnifiedMatchContext from './UnifiedMatchContext';
import UnifiedMatchReducer from './UnifiedMatchReducer';
import AuthContext from '../auth/authContext';
import DeckContext from '../decks/deckContext';
import TeamContext from '../teams/teamContext';
import { 
  getLocalTCGMatches, 
  getLocalVGCMatches, 
  deleteLocalTCGMatch, 
  deleteLocalVGCMatch,
  updateLocalTCGMatch,
  updateLocalVGCMatch,
  syncLocalDataToDatabase,
  hasLocalData
} from '../../utils/localStorageUtils';
import { toast } from 'react-toastify';
import {
  GET_TCG_MATCHES,
  GET_VGC_MATCHES,
  ADD_TCG_MATCH,
  ADD_VGC_MATCH,
  DELETE_TCG_MATCH,
  DELETE_VGC_MATCH,
  UPDATE_TCG_MATCH,
  UPDATE_VGC_MATCH,
  MATCH_ERROR,
  CLEAR_MATCHES,
  SET_LOADING
} from '../types';

const UnifiedMatchState = (props) => {
  const initialState = {
    tcgMatches: [],
    vgcMatches: [],
    loading: true,
    error: null
  };

  const [state, dispatch] = useReducer(UnifiedMatchReducer, initialState);
  const { user, setOnLoginSuccessCallback, loading: authLoading, isAuthenticated } = useContext(AuthContext);
  
  // Add refs to track if initial fetch has been done to prevent multiple fetches
  const tcgFetchedRef = React.useRef(false);
  const vgcFetchedRef = React.useRef(false);
  
  // Safely get context functions with fallbacks
  const deckContext = useContext(DeckContext);
  const teamContext = useContext(TeamContext);
  const addDeck = deckContext?.addDeck || (() => Promise.resolve());
  const addTeam = teamContext?.addTeam || (() => Promise.resolve());
  const getDecks = deckContext?.getDecks || (() => Promise.resolve());
  const getTeams = teamContext?.getTeams || (() => Promise.resolve());

  // Get TCG Matches
  const getTCGMatches = async () => {
    try {
      setLoading();
      
      // Don't make decisions while auth is still loading
      if (authLoading) {
        console.log('Auth still loading, deferring TCG matches fetch');
        return;
      }
      
      // Check if user is authenticated before making API call
      if (isAuthenticated && user && localStorage.getItem('token')) {
        try {
          // User is authenticated, try to get API matches
          const res = await api.get('/api/tcgmatches');
          
          dispatch({ 
            type: GET_TCG_MATCHES, 
            payload: res.data 
          });
        } catch (apiErr) {
          // If API fails, fall back to local matches
          console.warn('API call failed, using local TCG matches:', apiErr.message);
          const localMatches = getLocalTCGMatches();
          console.log('Local matches:', { matchCount: localMatches?.length || 0 });
          dispatch({ 
            type: GET_TCG_MATCHES, 
            payload: localMatches 
          });
        }
      } else {
        // User not authenticated, use local matches only
        console.log('User not authenticated, using local TCG matches');
        const localMatches = getLocalTCGMatches();
        console.log('Local matches:', { matchCount: localMatches?.length || 0 });
        dispatch({ 
          type: GET_TCG_MATCHES, 
          payload: localMatches 
        });
      }
    } catch (err) {
      console.error('Error in getTCGMatches:', err);
      dispatch({ 
        type: MATCH_ERROR, 
        payload: err.message || 'Error fetching TCG matches' 
      });
    }
  };

  // Get VGC Matches
  const getVGCMatches = async () => {
    try {
      setLoading();
      
      // Don't make decisions while auth is still loading
      if (authLoading) {
        console.log('Auth still loading, deferring VGC matches fetch');
        return;
      }
      
      // Check if user is authenticated before making API call
      if (isAuthenticated && user && localStorage.getItem('token')) {
        try {
          // User is authenticated, try to get API matches
          const res = await api.get('/api/vgcmatches');
          
          console.log('[UnifiedMatchState] VGC matches from API:', res.data?.length || 0);
          dispatch({ 
            type: GET_VGC_MATCHES, 
            payload: res.data 
          });
        } catch (apiErr) {
          // If API fails, fall back to local matches
          console.warn('API call failed, using local VGC matches:', apiErr.message);
          const localMatches = getLocalVGCMatches();
          dispatch({ 
            type: GET_VGC_MATCHES, 
            payload: localMatches 
          });
        }
      } else {
        // User not authenticated, use local matches only
        console.log('User not authenticated, using local VGC matches');
        const localMatches = getLocalVGCMatches();
        console.log('[UnifiedMatchState] Local VGC matches:', localMatches?.length || 0);
        dispatch({ 
          type: GET_VGC_MATCHES, 
          payload: localMatches 
        });
      }
    } catch (err) {
      dispatch({ 
        type: MATCH_ERROR, 
        payload: err.message || 'Error fetching VGC matches' 
      });
    }
  };

  // Add TCG Match
  const addTCGMatch = async (match) => {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      setLoading();
      const res = await api.post('/api/tcgmatches', match, config);
      
      dispatch({ 
        type: ADD_TCG_MATCH, 
        payload: res.data 
      });
      
      // Automatically refresh TCG matches after successful add
      setTimeout(() => {
        getTCGMatches();
        
        // If getDecks is available, refresh decks too (they might have new stats)
        if (getDecks) {
          getDecks();
        }
        
        // Trigger analytics refresh if available
        if (typeof window !== 'undefined' && window.refreshTCGDeckAnalytics) {
          console.log('UnifiedMatchState: Triggering TCG deck analytics refresh after match add');
          window.refreshTCGDeckAnalytics();
        }
      }, 100);
      
      return { success: true, data: res.data };
    } catch (err) {
      console.error('Error adding TCG match:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.msg || err.message || 'Error adding TCG match';
      dispatch({ 
        type: MATCH_ERROR, 
        payload: errorMessage 
      });
      return { success: false, error: errorMessage };
    }
  };

  // Add VGC Match
  const addVGCMatch = async (match) => {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      setLoading();
      const res = await api.post('/api/vgcmatches', match, config);
      
      dispatch({ 
        type: ADD_VGC_MATCH, 
        payload: res.data 
      });
      
      // Automatically refresh VGC matches after successful add
      setTimeout(() => {
        getVGCMatches();
        
        // If getTeams is available, refresh teams too (they might have new stats)
        if (getTeams) {
          getTeams();
        }
        
        // Trigger analytics refresh if available
        if (typeof window !== 'undefined' && window.refreshVGCTeamAnalytics) {
          console.log('UnifiedMatchState: Triggering VGC team analytics refresh after match add');
          window.refreshVGCTeamAnalytics();
        }
      }, 100);
      
      return { success: true, data: res.data };
    } catch (err) {
      console.error('Error adding VGC match:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.msg || err.message || 'Error adding VGC match';
      dispatch({ 
        type: MATCH_ERROR, 
        payload: errorMessage 
      });
      return { success: false, error: errorMessage };
    }
  };

  // Update TCG Match
  const updateTCGMatch = async (match) => {
    try {
      setLoading();
      
      // Check if it's a local match
      if (match._id.startsWith('local_')) {
        const updatedMatch = updateLocalTCGMatch(match._id, match);
        dispatch({ 
          type: UPDATE_TCG_MATCH, 
          payload: updatedMatch 
        });
      } else {
        // API match - send to server
        const config = {
          headers: {
            'Content-Type': 'application/json'
          }
        };
        
        const res = await api.put(`/api/tcgmatches/${match._id}`, match, config);
        
        dispatch({ 
          type: UPDATE_TCG_MATCH, 
          payload: res.data 
        });
      }
      
      // Automatically refresh data after successful update
      setTimeout(() => {
        getTCGMatches();
        
        // If getDecks is available, refresh decks too (stats might have changed)
        if (getDecks) {
          getDecks();
        }
        
        // Trigger analytics refresh if available
        if (typeof window !== 'undefined' && window.refreshTCGDeckAnalytics) {
          console.log('UnifiedMatchState: Triggering TCG deck analytics refresh after match update');
          window.refreshTCGDeckAnalytics();
        }
      }, 100);
    } catch (err) {
      dispatch({ 
        type: MATCH_ERROR, 
        payload: err.response?.data?.msg || err.message || 'Error updating TCG match' 
      });
      throw err; // Re-throw the error so calling code knows the update failed
    }
  };

  // Update VGC Match
  const updateVGCMatch = async (match) => {
    try {
      setLoading();
      
      // Check if it's a local match
      if (match._id.startsWith('local_')) {
        const updatedMatch = updateLocalVGCMatch(match._id, match);
        dispatch({ 
          type: UPDATE_VGC_MATCH, 
          payload: updatedMatch 
        });
      } else {
        // API match - send to server
        const config = {
          headers: {
            'Content-Type': 'application/json'
          }
        };
        
        const res = await api.put(`/api/vgcmatches/${match._id}`, match, config);
        
        dispatch({ 
          type: UPDATE_VGC_MATCH, 
          payload: res.data 
        });
      }
      
      // Automatically refresh data after successful update
      setTimeout(() => {
        getVGCMatches();
        
        // If getTeams is available, refresh teams too (stats might have changed)
        if (getTeams) {
          getTeams();
        }
        
        // Trigger analytics refresh if available
        if (typeof window !== 'undefined' && window.refreshVGCTeamAnalytics) {
          console.log('UnifiedMatchState: Triggering VGC team analytics refresh after match update');
          window.refreshVGCTeamAnalytics();
        }
      }, 100);
    } catch (err) {
      dispatch({ 
        type: MATCH_ERROR, 
        payload: err.response?.data?.msg || err.message || 'Error updating VGC match' 
      });
      throw err; // Re-throw the error so calling code knows the update failed
    }
  };

  // Delete TCG Match (handles both API and local storage)
  const deleteTCGMatch = async (id) => {
    try {
      console.log('UnifiedMatchState: Deleting TCG match:', id);
      
      // Check if it's a local match
      if (id.startsWith('local_')) {
        deleteLocalTCGMatch(id);
        dispatch({ 
          type: DELETE_TCG_MATCH, 
          payload: id 
        });
      } else {
        // Try to delete from API
        await api.delete(`/api/tcgmatches/${id}`);
        dispatch({ 
          type: DELETE_TCG_MATCH, 
          payload: id 
        });
      }
      
      console.log('UnifiedMatchState: TCG match deleted successfully, triggering analytics refresh');
      
      // Trigger a refresh of deck analytics if available
      setTimeout(() => {
        getTCGMatches();
        
        // If getDecks is available, refresh decks too
        if (getDecks) {
          getDecks();
        }
        
        if (typeof window !== 'undefined' && window.refreshTCGDeckAnalytics) {
          console.log('UnifiedMatchState: Triggering TCG deck analytics refresh after match deletion');
          window.refreshTCGDeckAnalytics();
        }
      }, 100);
      
    } catch (err) {
      console.error('UnifiedMatchState: Error deleting TCG match:', err);
      dispatch({ 
        type: MATCH_ERROR, 
        payload: err.response?.data?.msg || 'Error deleting TCG match' 
      });
    }
  };

  // Delete VGC Match (handles both API and local storage)
  const deleteVGCMatch = async (id) => {
    try {
      console.log('UnifiedMatchState: Deleting VGC match:', id);
      
      // Check if it's a local match
      if (id.startsWith('local_')) {
        deleteLocalVGCMatch(id);
        dispatch({ 
          type: DELETE_VGC_MATCH, 
          payload: id 
        });
      } else {
        // Try to delete from API
        await api.delete(`/api/vgcmatches/${id}`);
        dispatch({ 
          type: DELETE_VGC_MATCH, 
          payload: id 
        });
      }
      
      console.log('UnifiedMatchState: VGC match deleted successfully, triggering analytics refresh');
      
      // Trigger a refresh of team analytics if available
      setTimeout(() => {
        getVGCMatches();
        
        // If getTeams is available, refresh teams too
        if (getTeams) {
          getTeams();
        }
        
        if (typeof window !== 'undefined' && window.refreshVGCTeamAnalytics) {
          console.log('UnifiedMatchState: Triggering VGC team analytics refresh after match deletion');
          window.refreshVGCTeamAnalytics();
        }
      }, 100);
      
    } catch (err) {
      console.error('UnifiedMatchState: Error deleting VGC match:', err);
      dispatch({ 
        type: MATCH_ERROR, 
        payload: err.response?.data?.msg || 'Error deleting VGC match' 
      });
    }
  };

  // Clear Matches
  const clearMatches = () => {
    dispatch({ type: CLEAR_MATCHES });
    // Reset fetch flags when clearing matches
    tcgFetchedRef.current = false;
    vgcFetchedRef.current = false;
  };

  // Set Loading
  const setLoading = () => {
    dispatch({ type: SET_LOADING });
  };

  // Sync local data (matches, decks, teams) to database
  const syncLocalData = async () => {
    // Check for authentication - either user state is available or token exists
    const hasToken = localStorage.getItem('token');
    const isAuthenticated = user || hasToken;
    
    if (!isAuthenticated) {
      console.log('User not authenticated, cannot sync local data');
      return;
    }
    
    // If we have a token but no user state yet, wait for user state to update
    if (hasToken && !user) {
      console.log('Token found but user state not ready, waiting...');
      
      // Wait longer with retry logic for user state to be updated
      let retries = 0;
      const maxRetries = 20; // Wait up to 2 seconds
      
      while (!user && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }
      
      // Check final state
      if (!user) {
        console.log('User state still not ready after retries, skipping sync');
        return;
      }
      
      console.log('User state ready after', retries * 100, 'ms');
    }

    if (!hasLocalData()) {
      console.log('No local data to sync');
      return;
    }

    try {
      console.log('Starting local data sync...');
      console.log('Sync context - User:', user?.username || 'no username', 'Token exists:', !!hasToken);
      toast.info('Syncing your local data to your account...', { autoClose: 3000 });
      
      // Get user ID from auth context
      const userId = user?._id;
      
      if (!userId) {
        console.error('Cannot sync: user ID not available');
        toast.error('Sync failed: user information not available');
        return;
      }
      
      // Create sync versions of the functions that force API usage
      const syncAddDeck = (deckData) => addDeck(deckData, true);
      const syncAddTeam = (teamData) => addTeam(teamData, true);
      
      const result = await syncLocalDataToDatabase(api, addTCGMatch, addVGCMatch, syncAddDeck, syncAddTeam, userId);
      
      if (result.errors.length === 0) {
        toast.success(`Successfully synced ${result.syncedCount} items to your account!`, { autoClose: 5000 });
        // Refresh all data to show the synced items
        getTCGMatches();
        getVGCMatches();
        getDecks(); // Refresh deck list to show synced decks
        getTeams(); // Refresh team list to show synced teams
      } else {
        toast.warn(`Synced ${result.syncedCount} of ${result.totalItems} items. Some items failed to sync.`, { autoClose: 7000 });
        console.warn('Sync errors:', result.errors);
        // Still refresh all contexts even if there were some errors
        getTCGMatches();
        getVGCMatches();
        getDecks();
        getTeams();
      }
    } catch (error) {
      console.error('Error syncing local data:', error);
      toast.error('Failed to sync some items. They will remain stored locally.', { autoClose: 5000 });
    }
  };

  // Register sync callback on component mount
  useEffect(() => {
    if (setOnLoginSuccessCallback) {
      setOnLoginSuccessCallback(syncLocalData);
    }
  }, [setOnLoginSuccessCallback]);

  // Re-fetch matches when auth loading is complete
  useEffect(() => {
    console.log('UnifiedMatchState auth useEffect:', {
      authLoading,
      isAuthenticated,
      hasUser: !!user,
      currentTcgMatchesLength: state.tcgMatches?.length || 0,
      tcgFetched: tcgFetchedRef.current,
      vgcFetched: vgcFetchedRef.current
    });
    
    if (!authLoading) {
      // Auth loading is complete. Defer the (potentially large) TCG + VGC match
      // fetches to idle time instead of firing them synchronously right after
      // auth — those requests and their JSON parsing previously blocked the main
      // thread during the first-interaction window, hurting INP. Data still lands
      // in context for consumers (Dashboard/analytics), just a beat after the
      // page is interactive. Refs guard against refetch loops.
      const runDeferred = (fn) => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(fn, { timeout: 2000 });
        } else {
          setTimeout(fn, 200);
        }
      };

      if (!tcgFetchedRef.current) {
        tcgFetchedRef.current = true;
        runDeferred(() => getTCGMatches());
      }
      if (!vgcFetchedRef.current) {
        vgcFetchedRef.current = true;
        runDeferred(() => getVGCMatches());
      }
    }
  }, [authLoading]);

  return (
    <UnifiedMatchContext.Provider
      value={{
        tcgMatches: state.tcgMatches,
        vgcMatches: state.vgcMatches,
        loading: state.loading,
        error: state.error,
        getTCGMatches,
        getVGCMatches,
        addTCGMatch,
        addVGCMatch,
        updateTCGMatch,
        updateVGCMatch,
        deleteTCGMatch,
        deleteVGCMatch,
        clearMatches,
        setLoading
      }}
    >
      {props.children}
    </UnifiedMatchContext.Provider>
  );
};

export default UnifiedMatchState;